import React, { useState, useEffect } from "react";
import APICall from "../../Utility/APICall";
import { paths } from "../../Utility/Constants"; // Using web version paths
import { useNavigate } from "react-router-dom";

const EditAgreementModal = ({
  isOpen,
  onClose,
  agreementData,
  onSave,
  jwt,
}) => {
  const navigate = useNavigate();
  const [showOptions, setShowOptions] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showModifyForm, setShowModifyForm] = useState(false);
  const [terminateDate, setTerminateDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Format date for API (YYYY-MM-DD HH:MM:SS)
  const formatDateForAPI = (date) => {
    if (!date) return null;

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Form data for modification
  const [formData, setFormData] = useState({
    rent: "",
    security_deposit: "",
    maintenance: "",
    due_date: "",
    tenure: "",
    staying_from: "",
    vacating_on: "",
  });

  // Update form data when agreementData changes
  useEffect(() => {
    if (agreementData) {
      const initialData = {
        rent: agreementData?.rent?.[0]?.rent || "",
        security_deposit: agreementData?.security_deposit || "",
        maintenance: agreementData?.maintenance || "",
        due_date: agreementData?.due_date || "",
        tenure: agreementData?.tenure || "",
        staying_from: formatDateForInput(agreementData?.staying_from),
        vacating_on: formatDateForInput(agreementData?.vacating_on),
      };

      // If tenure is not Custom and we have staying_from, calculate vacating_on
      if (initialData.tenure !== "Custom" && initialData.staying_from) {
        initialData.vacating_on = calculateVacatingDate(
          initialData.staying_from,
          initialData.tenure
        );
      }

      setFormData(initialData);
    }
  }, [agreementData]);

  // Calculate vacating date based on staying_from and tenure
  const calculateVacatingDate = (stayingFrom, tenure) => {
    if (!stayingFrom || !tenure || tenure === "Custom") return "";

    try {
      const startDate = new Date(stayingFrom);
      if (isNaN(startDate.getTime())) return "";

      const tenureValue = parseInt(tenure.split(" ")[0]);
      if (isNaN(tenureValue)) return "";

      // Add months to the start date
      const vacatingDate = new Date(startDate);
      vacatingDate.setMonth(vacatingDate.getMonth() + tenureValue);

      return formatDateForInput(vacatingDate);
    } catch (error) {
      console.error("Error calculating vacating date:", error);
      return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "tenure" || name === "staying_from") {
      const newFormData = { ...formData, [name]: value };

      // Auto-calculate vacating date when tenure or staying_from changes
      if (name === "tenure" || name === "staying_from") {
        const stayingFrom =
          name === "staying_from" ? value : formData.staying_from;
        const tenure = name === "tenure" ? value : formData.tenure;

        if (tenure !== "Custom") {
          newFormData.vacating_on = calculateVacatingDate(stayingFrom, tenure);
        }
      }

      setFormData(newFormData);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleModifyAgreement = () => {
    setShowOptions(false);
    setShowModifyForm(true);
  };

  const handleTerminateAgreement = () => {
    setShowOptions(false);
    setShowDatePicker(true);
  };

  const handleRenewAgreement = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to renew this agreement? This will extend the tenure by ${agreementData?.tenure} from end date.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setError(null);

      console.log("Renewing agreement with ID:", agreementData.entity_id);

      const response = await APICall(
        "PUT",
        paths.agreement.renew,
        {
          agreement_id: agreementData.entity_id, // Use entity_id from agreement data
        },
        undefined,
        jwt
      );

      console.log("Agreement renewed successfully:", response);

      if (response && response.response) {
        onSave(response.response.agreement);
        onClose();
      }
    } catch (err) {
      console.error("Error renewing agreement:", err);
      setError("There was an error renewing your agreement");
    } finally {
      setLoading(false);
    }
  };

  // const handleSaveModification = async () => {
  //   try {
  //     setLoading(true);
  //     setError(null);

  //     // Format dates for API
  //     const formattedData = {
  //       ...formData,
  //       staying_from: formatDateForAPI(formData.staying_from),
  //       vacating_on: formatDateForAPI(formData.vacating_on),
  //     };

  //     // console.log("Updating agreement with ID:", agreementData.entity_id);
  //     // console.log("Update data:", formattedData);

  //     console.log(
  //       "Preparing to modify agreement with ID:",
  //       agreementData.entity_id
  //     );

  //     // Close modal and navigate to AddMoreTenant with modification data
  //     // onClose();

  //     // const response = await APICall(
  //     //   "PUT",
  //     //   paths.agreement.update,
  //     //   {
  //     //     agreement_id: agreementData.entity_id, // Use entity_id from agreement data
  //     //     ...formattedData,
  //     //   },
  //     //   undefined,
  //     //   jwt
  //     // );

  //     // console.log("Agreement updated successfully:", response);

  //     // if (response && response.response) {
  //     //   onSave(response.response.agreement);
  //     //   onClose();
  //     // }
  //     navigate(
  //       `/dashboard/agreements/${agreementData.entity_id}/modify/review-tenants`,
  //       {
  //         state: {
  //           modifiedAgreementData: {
  //             ...agreementData,
  //             ...formattedData,
  //           },
  //           originalAgreement: agreementData,
  //           isModifying: true,
  //         },
  //       }
  //     );
  //   } catch (err) {
  //     // console.error("Error updating agreement:", err);
  //     // setError("There was an error updating your agreement");
  //     console.error("Error preparing modification:", err);
  //     setError("There was an error preparing the modification");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Replace the existing handleSaveModification function in EditAgreementModal.jsx with this:

  // Enhanced handleSaveModification with debugging in EditAgreementModal.jsx

  const handleSaveModification = async () => {
    try {
      setLoading(true);
      setError(null);

      // Format dates for API
      const formattedData = {
        ...formData,
        staying_from: formatDateForAPI(formData.staying_from),
        vacating_on: formatDateForAPI(formData.vacating_on),
      };

      // Prepare navigation data
      const navigationData = {
        modifiedAgreementData: {
          ...agreementData,
          ...formattedData,
        },
        originalAgreement: agreementData,
        isModifying: true,
      };

      const targetRoute = `/dashboard/agreements/${agreementData.entity_id}/modify/review-tenants`;

      console.log("🚀 Starting Agreement Modification Flow");
      console.log("📋 Agreement ID:", agreementData.entity_id);
      console.log("🎯 Target Route:", targetRoute);
      console.log("📦 Navigation State:", navigationData);
      console.log("📝 Form Data:", formattedData);

      // Close modal first
      onClose();

      // Small delay to ensure modal closes before navigation
      setTimeout(() => {
        console.log("🔄 Attempting navigation...");

        try {
          navigate(targetRoute, {
            state: navigationData,
          });

          console.log("✅ Navigation call completed");

          // Additional debug: Check if URL actually changed
          setTimeout(() => {
            console.log(
              "🔍 Current URL after navigation:",
              window.location.pathname
            );
            console.log("🔍 Current search:", window.location.search);
          }, 100);
        } catch (navError) {
          console.error("❌ Navigation error:", navError);
          setError("Failed to navigate to tenant review page");
          setLoading(false);
        }
      }, 250);
    } catch (err) {
      console.error("❌ Error preparing modification:", err);
      setError("There was an error preparing the modification");
      setLoading(false);
    }
  };

  const handleConfirmTermination = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to terminate this agreement?"
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setError(null);

      // Validate we have the required data
      if (!agreementData?.entity_id) {
        throw new Error("Agreement ID is missing");
      }

      console.log("Terminating agreement with ID:", agreementData.entity_id);
      console.log("Termination date:", terminateDate);

      const requestBody = {
        agreement_id: agreementData.entity_id,
        terminated_on: terminateDate,
      };

      console.log("Request body:", requestBody);

      const response = await APICall(
        "PUT", // Method for terminate (based on mobile routes)
        paths.agreement.terminate, // Path string
        requestBody,
        undefined, // query params
        jwt
      );

      console.log("Agreement termination response:", response);

      // Check if response indicates success
      if (response && response.status !== "error") {
        onSave(null); // Agreement terminated, so no agreement data
        onClose();
        window.location.reload();
      } else {
        // Handle API error response
        const errorMessage =
          response?.response || response?.message || "Unknown error occurred";
        alert(errorMessage);
        console.log("Error terminating agreement:", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error("Error terminating agreement:", err);
      setError(err.message || "There was an error terminating your agreement");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setShowOptions(true);
    setShowModifyForm(false);
    setShowDatePicker(false);
    setError(null);
  };

  if (!isOpen) return null;

  const canRenew =
    agreementData?.tenure &&
    agreementData.tenure !== "Custom" &&
    (agreementData.tenure.split(" ")[1] === "Months" ||
      agreementData.tenure.split(" ")[1] === "Month");

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {showOptions
              ? "Edit Agreement"
              : showModifyForm
                ? "Modify Terms"
                : "Terminate Agreement"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
              Processing...
            </div>
          )}

          {/* Options View */}
          {showOptions && (
            <div className="space-y-3">
              <button
                onClick={handleModifyAgreement}
                className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 flex justify-between items-center"
                disabled={loading}
              >
                <span>Modify Terms</span>
                <span className="text-gray-400">→</span>
              </button>

              {canRenew && (
                <button
                  onClick={handleRenewAgreement}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 flex justify-between items-center"
                  disabled={loading}
                >
                  <span>Renew for {agreementData.tenure}</span>
                  <span className="text-gray-400">→</span>
                </button>
              )}

              <button
                onClick={handleTerminateAgreement}
                className="w-full text-left p-4 border border-red-200 rounded-lg hover:bg-red-50 flex justify-between items-center text-red-600"
                disabled={loading}
              >
                <span>Terminate</span>
                <span className="text-red-400">→</span>
              </button>

              {!canRenew && agreementData?.tenure === "Custom" && (
                <p className="text-sm text-gray-500 mt-4">
                  You cannot renew a custom tenure agreement
                </p>
              )}
            </div>
          )}

          {/* Modify Form */}
          {showModifyForm && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Rent
                </label>
                <input
                  type="number"
                  name="rent"
                  value={formData.rent}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Security Deposit
                </label>
                <input
                  type="number"
                  name="security_deposit"
                  value={formData.security_deposit}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maintenance
                </label>
                <input
                  type="number"
                  name="maintenance"
                  value={formData.maintenance}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <input
                  type="number"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  min="1"
                  max="31"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tenure
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {["3 Months", "6 Months", "11 Months", "Custom"].map(
                    (option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() =>
                          handleChange({
                            target: { name: "tenure", value: option },
                          })
                        }
                        className={`p-3 border rounded-lg text-center transition-colors ${
                          formData.tenure === option
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                        disabled={loading}
                      >
                        {option}
                      </button>
                    )
                  )}
                </div>
                {formData.vacating_on && formData.tenure !== "Custom" && (
                  <p className="text-sm text-gray-600 mt-2">
                    Vacating on:{" "}
                    {new Date(formData.vacating_on).toLocaleDateString(
                      "en-GB",
                      {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      }
                    )}
                  </p>
                )}
              </div>

              {/* Warning message like in mobile UI */}
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-red-400">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      Lease coincides with previous agreements. This might cause
                      confusion with rent summary.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">Advance Rent</h4>
                <p className="text-sm text-gray-500">
                  You cannot modify advance rent after agreement creation
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Staying From
                </label>
                <input
                  type="date"
                  name="staying_from"
                  value={formData.staying_from}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
                  disabled={false} // Disabled as per mobile UI - cannot modify after agreement creation
                />
                <p className="text-xs text-gray-500 mt-1">
                  You cannot modify staying from date after agreement creation
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vacating On
                </label>
                <input
                  type="date"
                  name="vacating_on"
                  value={formData.vacating_on}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
                  disabled={true} // Disabled - auto-calculated based on tenure
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">
                  Automatically calculated based on tenure
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleBack}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  onClick={handleSaveModification}
                  className="flex-1 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  disabled={loading}
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Terminate Date Picker */}
          {showDatePicker && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Termination Date
                </label>
                <input
                  type="date"
                  value={terminateDate}
                  onChange={(e) => setTerminateDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                  disabled={loading}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleBack}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  onClick={handleConfirmTermination}
                  className="flex-1 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
                  disabled={loading}
                >
                  Terminate Agreement
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditAgreementModal;
